import { format } from 'date-fns';
import { camelCase, isArray, isDate, isNaN, isObject, snakeCase, transform } from 'lodash';
import { GreenerUnit } from 'types';
import { SHORT_DATE_TIME_FORMAT } from './constants';
import { getDateLocale } from './localization';

export const getAssetLabel = (asset: Partial<GreenerUnit> | null | undefined): string => {
  return `${asset?.externalIdentifier ?? '-'} | ${asset?.name ?? '-'}`;
};

/**
 * Returns the same array of objects with each tsStart and tsEnd parsed as Date javascript objects
 * @param {Array.<Object>|Object} data
 * @param {string} data.tsStart
 * @param {string} data.tsEnd
 * @returns {Array.<Object>}
 */
export function parseTsStartAndTsEnd<
  T extends { tsStart: Date | string | number; tsEnd?: Date | string | number | null },
>(data: T[] | T): T[] | T {
  let isArgumentArray = true;
  if (!(data instanceof Array)) {
    data = [data];
    isArgumentArray = false;
  }

  const result = data?.map((obj) => {
    return {
      ...obj,
      tsStart: new Date(obj.tsStart),
      tsEnd: obj.tsEnd ? new Date(obj.tsEnd) : null,
    };
  }) as T[];

  if (!isArgumentArray && result?.length > 0) {
    return result[0];
  }

  return result;
}

/**
 * Returns a string representation of a given date. Returns '-' for null.
 * @param {Date|string|number} dt Date object or number or string representing a date
 * @param dtFormat the format of the date string to be returned. Default 'yyyy-MM-dd HH:mm'
 * @returns {string} String representation of the date.
 */
export function representDate(
  dt: Date | string | number | null | undefined,
  dtFormat: string = SHORT_DATE_TIME_FORMAT
): string {
  if (!dt) {
    return '-';
  }

  return format(new Date(dt), dtFormat, {
    locale: getDateLocale(),
  });
}

/**
 * Returns a string in which an energy is represented with an energy unit.
 * @param {number} num representing energy
 * @returns {string} formatted number with unit
 */
export function formatEnergyWithUnit(num: number | null | undefined): string {
  if (typeof num === 'number' && !isNaN(num)) {
    return Math.abs(Math.round(num)) > 999 ? (num / 1000).toFixed(1) + ' MWh' : num.toFixed() + ' kWh';
  } else {
    return '- kWh';
  }
}

/**
 * Returns a string with a percentage value.
 * @param {number} num representing a percentage
 * @returns {string} formatted number with unit
 */
export function formatPercentage(num: number | null | undefined, decimals = 1): string {
  if (typeof num === 'number' && !isNaN(num)) {
    return `${Math.round(num * 100, decimals)} %`;
  } else {
    return '- %';
  }
}

export function formatCoordinateAsPoint({
  lat,
  lng,
}: {
  lat?: number | null;
  lng?: number | null;
}): { type: 'Point'; coordinates: [number, number] } | undefined {
  if (!lng || !lat) {
    return;
  }

  return { type: 'Point', coordinates: [lng, lat] };
}

export const buildGraphConfigKey = (field: string | number, agg: string | number): string => {
  return field + '_' + agg;
};

export const keysToCamelCase = <T>(obj: T): T | undefined => {
  if (!obj) {
    return;
  }
  return transform(obj, (acc, value, key, target) => {
    const camelKey = isArray(target) ? key : camelCase(key);

    acc[camelKey] = isObject(value) && !isDate(value) ? keysToCamelCase(value) : value;
  }) as T;
};

export const keysToSnakeCase = <T>(obj: T): T | undefined => {
  if (!obj) {
    return;
  }
  return transform(obj, (acc, value, key, target) => {
    const snakeKey = isArray(target) ? key : snakeCase(key);

    acc[snakeKey] = isObject(value) && !isDate(value) ? keysToSnakeCase(value) : value;
  }) as T;
};

//Format with dot notation every 3 digits - design choice
export const formatNumber = (number: unknown): unknown => {
  if (typeof number !== 'number') {
    return number;
  }
  return new Intl.NumberFormat('de-DE').format(number);
};

export const truncateText = ({
  text,
  maxChars = 400,
  fallbackMessage = '',
}: { text?: string; maxChars?: number; fallbackMessage?: string } = {}): string => {
  if (!text) {
    return fallbackMessage;
  }

  let result = text.trim();

  if (result.length > maxChars) {
    result = `${result.slice(0, maxChars)} ...`;
  }

  return result || fallbackMessage;
};

export const replaceSpacesWithUnderscores = (value: string): string => {
  return value.trim().replace(/\s+/g, '_');
};
